import { defineStore } from "pinia";
import { createRoom, joinRoom, getCurrentRoom, getRoomState, leaveRoom, setRoomMode } from "@/services/api";
import { clearRoomIntent, rememberRoomIntent, requestId, roomTokenFromLaunch, savedRoomIntent, validRoomToken } from "@/services/roomIntent";

export const ROOM_ERRORS = Object.freeze({
  ROOM_EXPIRED: "Срок ссылки истёк. Попросите друга создать новую комнату.",
  ROOM_FULL: "В комнате уже два игрока. Попросите новую ссылку.",
  ROOM_CLOSED: "Эта комната закрыта. Создайте новую игру с другом.",
  ROOM_NOT_FOUND: "Комната не найдена. Проверьте ссылку приглашения.",
  SESSION_BUSY: "Сначала завершите текущую игру или покиньте другую комнату.",
  ROOM_MODE_LOCKED: "Друг уже подключился. Режим зафиксирован до конца раунда.",
  ROOM_HOST_ONLY: "Режим комнаты может менять только её хозяин.",
  INVALID_MODE: "Не удалось выбрать этот режим. Попробуйте ещё раз.",
  NOT_CONNECTED: "Соединение с сервером ещё не готово. Попробуйте снова.",
});

export function roomErrorText(error) {
  return ROOM_ERRORS[error?.code] || (error?.status === 404 ? ROOM_ERRORS.ROOM_NOT_FOUND : error?.message) || "Не удалось открыть комнату. Попробуйте снова.";
}

const entering = new WeakMap();
const opening = new WeakMap();
const changingMode = new WeakMap();

export const useRoomStore = defineStore("room", {
  state: () => ({ snapshot: null, inviteToken: savedRoomIntent(), pending: false, modePending: false, error: "", operationId: null, operationKind: null }),
  getters: { active: (state) => !!state.snapshot && state.snapshot.state !== "CLOSED" },
  actions: {
    captureLaunchIntent(launch) {
      const token = roomTokenFromLaunch(launch);
      if (token) this.setIntent(token);
      return this.inviteToken;
    },
    setIntent(token) {
      if (!validRoomToken(token)) return false;
      if (token !== this.inviteToken) { this.operationId = null; this.operationKind = null; }
      this.inviteToken = token;
      rememberRoomIntent(token);
      return true;
    },
    clearIntent() { this.inviteToken = null; clearRoomIntent(); },
    applySnapshot(value) {
      if (!value || !value.roomId || !["WAITING", "PAIRED", "CLOSED"].includes(value.state)) return false;
      const previous = this.snapshot;
      if (previous?.roomId === value.roomId && Number(value.revision) < Number(previous.revision)) return false;
      if (previous?.roomId !== value.roomId) this.modePending = false;
      this.snapshot = { ...value, receivedAt: Date.now() };
      this.error = "";
      return true;
    },
    // Coalesce taps, remounts and retries instead of creating another invitation.
    async openHost(mode) {
      if (opening.has(this)) return opening.get(this);
      const task = (async () => {
        await this.restore();
        return this.active ? this.snapshot : this.enter(mode);
      })();
      opening.set(this, task);
      try { return await task; } finally { opening.delete(this); }
    },
    async enter(mode, token = null) {
      if (entering.has(this)) return entering.get(this);
      if (!token && this.active) return this.snapshot;
      if (this.snapshot?.state === "CLOSED") { this.operationId = null; this.operationKind = null; this.snapshot = null; }
      const kind = token ? "join:" + token : "create:" + mode;
      if (this.operationKind !== kind) { this.operationId = requestId(); this.operationKind = kind; }
      this.pending = true;
      this.error = "";
      const task = (async () => {
        try {
          const value = token ? await joinRoom(token, this.operationId) : await createRoom(mode, this.operationId);
          this.applySnapshot(value);
          this.clearIntent();
          return this.snapshot;
        } catch (error) {
          // A previous create may have completed while its response was lost.
          if (!token && error?.code === "SESSION_BUSY") {
            await this.restore().catch(() => null);
            if (this.active) return this.snapshot;
          }
          this.error = roomErrorText(error);
          throw error;
        } finally { this.pending = false; }
      })();
      entering.set(this, task);
      try { return await task; } finally { entering.delete(this); }
    },
    async changeMode(mode) {
      const roomId = this.snapshot?.roomId;
      const previous = changingMode.get(this);
      if (previous?.roomId === roomId) return previous.task;
      if (!roomId || this.snapshot.state !== "WAITING" || !this.snapshot.inviteToken) return this.snapshot;
      if (mode === this.snapshot.gameMode) return this.snapshot;
      this.modePending = true;
      this.error = "";
      const task = (async () => {
        try {
          const value = await setRoomMode(roomId, mode);
          if (this.snapshot?.roomId === roomId) this.applySnapshot(value);
          return this.snapshot;
        } catch (error) {
          if (this.snapshot?.roomId === roomId) {
            await this.refresh().catch(() => null);
            if (this.snapshot?.roomId === roomId) this.error = roomErrorText(error);
          }
          throw error;
        }
      })();
      const operation = { roomId, task };
      changingMode.set(this, operation);
      try { return await task; }
      finally {
        if (changingMode.get(this) === operation) { changingMode.delete(this); this.modePending = false; }
      }
    },
    async restore() {
      const previous = this.snapshot;
      const value = await getCurrentRoom();
      // An older request must not resurrect a room after a new invitation or reset.
      if (this.snapshot !== previous && this.snapshot?.roomId !== value?.roomId) return this.snapshot;
      if (value) this.applySnapshot(value);
      else if (this.snapshot?.state !== "CLOSED") this.snapshot = null;
      return this.snapshot;
    },
    async refresh() {
      const roomId = this.snapshot?.roomId;
      if (!roomId) return this.restore();
      const value = await getRoomState(roomId);
      if (this.snapshot?.roomId === roomId) this.applySnapshot(value);
      return this.snapshot;
    },
    async leave() {
      const roomId = this.snapshot?.roomId;
      const value = roomId ? await leaveRoom(roomId) : null;
      if (this.snapshot?.roomId !== roomId) return this.snapshot;
      if (value) this.applySnapshot(value);
      this.clearIntent();
      this.operationId = null;
      this.operationKind = null;
      return this.snapshot;
    },
    reset() { this.snapshot = null; this.modePending = false; this.error = ""; this.operationId = null; this.operationKind = null; this.clearIntent(); },
  },
});
